import { useId, type ReactNode } from "react";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { fadeIn } from "../lib/motion";

export interface TabItem<T extends string> {
  id: T;
  label: string;
  icon?: LucideIcon;
  /** Shown as a small count chip after the label. */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps<T extends string> {
  tabs: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
}

/**
 * Horizontally scrollable underline tabs. The gold underline slides between
 * tabs via a shared layout element, and the strip scrolls rather than wraps on
 * narrow screens so the row never becomes two lines.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  className,
}: TabsProps<T>) {
  const layoutId = useId();

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "no-scrollbar -mx-1 flex gap-1 overflow-x-auto border-b border-line px-1",
        className,
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.id === value;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={selected}
            aria-controls={`${layoutId}-${tab.id}`}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={cn(
              "relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap px-3.5 pb-3 pt-2.5 text-sm font-medium",
              "transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500",
              "disabled:cursor-not-allowed disabled:opacity-40",
              selected
                ? "text-content"
                : "text-content-subtle hover:text-content-muted",
            )}
          >
            {Icon && <Icon size={15} aria-hidden="true" />}
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular",
                  selected
                    ? "bg-gold-100 text-gold-800 dark:bg-gold-950/70 dark:text-gold-300"
                    : "bg-surface-sunken text-content-subtle",
                )}
              >
                {tab.count}
              </span>
            )}
            {selected && (
              <motion.span
                layoutId={layoutId}
                aria-hidden="true"
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-gold-sheen"
                transition={{ type: "spring", stiffness: 420, damping: 34 }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  children,
  tabKey,
  className,
}: {
  children: ReactNode;
  /** Change this when the tab changes so the panel re-animates. */
  tabKey: string;
  className?: string;
}) {
  return (
    <motion.div
      key={tabKey}
      role="tabpanel"
      variants={fadeIn}
      initial="hidden"
      animate="visible"
      className={cn("pt-6", className)}
    >
      {children}
    </motion.div>
  );
}
